<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import SearchBar from '@/components/ui/SearchBar.vue';
import DateRangeModal from '@/components/shared/DateRangeModal.vue';
import MoreOptionsModal from '@/components/shared/MoreOptionsModal.vue';
import { usePlayerAvailableValuesStore } from '@/stores/playerAvailableValues';
import { useFilterConfigurationStore } from '@/stores/filterConfiguration';
import { loadSearchWidgetDefaults } from '@/composables/filters/useSearchWidgetDefaults';
import { loadIbeFilterConfiguration } from '@/composables/filters/useFilterConfiguration';
import { UrlParameterService } from '@/services/url/urlParameterService';
import type { DateRange, RoomOccupancy } from '@/types/SearchBar';
import type { DurationMode } from '@/utils/filters/durationMode';

interface Props {
  targetUrl?: string;
}

const props = withDefaults(defineProps<Props>(), {
  targetUrl: '',
});

const availableValuesStore = usePlayerAvailableValuesStore();
const configStore = useFilterConfigurationStore();

const { initialState, disabledFields, defaults } = loadSearchWidgetDefaults();
const selectedCountries = ref<string[]>(initialState.selectedCountries);
const dateRange = ref<DateRange>(initialState.dateRange);
const duration = ref<number>(initialState.duration);
const roomOccupancy = ref<RoomOccupancy>(initialState.roomOccupancy);

const isDestForced = disabledFields.includes('dest');
const destInteracted = ref(false);
const selectedCountriesForBar = computed(() =>
  destInteracted.value || isDestForced ? selectedCountries.value : []
);

function handleCountriesChange(ids: string[]) {
  destInteracted.value = true;
  selectedCountries.value = ids;
}

const filterConfig = loadIbeFilterConfiguration();
if (filterConfig) configStore.loadFromFilterConfiguration(filterConfig);
if (defaults) configStore.loadFromDefaults(defaults);

onMounted(() => {
  availableValuesStore.fetchAvailableValues();
});

const durationMode = ref<DurationMode>('preset');

const dateConstraints = computed(() => ({
  min: availableValuesStore.dateRangeForInput.min || undefined,
  max: availableValuesStore.dateRangeForInput.max || undefined,
}));

function handleDateRangeChange(range: DateRange) {
  dateRange.value = range;
}

function handleSearchClick() {
  if (!props.targetUrl) {
    console.warn('[SearchWidgetPage] No data-target-url configured on embed element');
    return;
  }

  const url = UrlParameterService.buildUrl(props.targetUrl, {
    destinationId: selectedCountries.value.length > 0 ? selectedCountries.value : undefined,
    startDate: dateRange.value?.[0] || undefined,
    endDate: dateRange.value?.[1] || undefined,
    duration: duration.value,
    durationMode: durationMode.value,
    roomOccupancy: roomOccupancy.value,
  });

  window.location.href = url;
}
</script>

<template>
  <SearchBar
    :countries="availableValuesStore.countries"
    :selected-country-ids="selectedCountriesForBar"
    :date-range="dateRange"
    :room-occupancy="roomOccupancy"
    :disabled-fields="disabledFields"
    :auto-detect-initial-values="false"
    @countries-change="handleCountriesChange"
    @room-occupancy-change="roomOccupancy = $event"
    @search-click="handleSearchClick"
  />

  <DateRangeModal
    :date-range="dateRange"
    :duration="duration"
    :duration-mode="durationMode"
    :min-date="dateConstraints.min"
    :max-date="dateConstraints.max"
    @update:date-range="handleDateRangeChange"
    @update:duration="duration = $event"
    @update:duration-mode="durationMode = $event"
  />

  <MoreOptionsModal />
</template>
